import { Trophy, Users, Target, Clock, Award, Globe, Heart, Shield, BookOpen, ChevronRight } from 'lucide-react';
import { useLanguage } from '@/components/LanguageContext';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';

const SportCultureArts = () => {
  const { t } = useLanguage();
  
  const programDetails = [
    t('programs.sportCultureArts.details.background', 'Our Sports, Culture & Arts program was established to nurture youth talents and preserve cultural heritage. Through various activities, we provide platforms for young people to express themselves and develop their skills.'),
    t('programs.sportCultureArts.details.empowerment', 'We focus on empowering youth through sports, cultural activities, and artistic expression, helping them build confidence and develop important life skills.'),
    t('programs.sportCultureArts.details.cultural', 'Our program plays a vital role in preserving and promoting Rwandan cultural heritage while embracing modern forms of artistic expression.'),
    t('programs.sportCultureArts.details.education', 'Through structured training and workshops, we provide youth with opportunities to learn and excel in their chosen fields of sports, culture, or arts.')
  ];

  const objectives = [
    {
      title: t('programs.sportCultureArts.objectives.main', 'Main Objective'),
      description: t('programs.sportCultureArts.objectives.mainDesc', 'To promote sports, culture, and arts among youth while preserving cultural heritage and developing talents.'),
      icon: <Target className="h-6 w-6" />
    },
    {
      title: t('programs.sportCultureArts.objectives.specific1', 'Sports Development'),
      description: t('programs.sportCultureArts.objectives.specific1Desc', 'To identify and nurture sporting talents among youth through structured training and competitions.'),
      icon: <Trophy className="h-6 w-6" />
    },
    {
      title: t('programs.sportCultureArts.objectives.specific2', 'Cultural Preservation'),
      description: t('programs.sportCultureArts.objectives.specific2Desc', 'To preserve and promote Rwandan cultural heritage through various activities and performances.'),
      icon: <Heart className="h-6 w-6" />
    },
    {
      title: t('programs.sportCultureArts.objectives.specific3', 'Artistic Expression'),
      description: t('programs.sportCultureArts.objectives.specific3Desc', 'To provide platforms for youth to express themselves through various art forms and develop their creative skills.'),
      icon: <BookOpen className="h-6 w-6" />
    }
  ];

  const targetGroups = [
    {
      title: t('programs.sportCultureArts.targetGroups.direct', 'Direct Beneficiaries'),
      groups: [
        t('programs.sportCultureArts.targetGroups.direct1', 'Children and youth aged 5-30 years'),
        t('programs.sportCultureArts.targetGroups.direct2', 'Local sports clubs and cultural groups'),
        t('programs.sportCultureArts.targetGroups.direct3', 'Artists and performers')
      ],
      icon: <Users className="h-6 w-6" />
    },
    {
      title: t('programs.sportCultureArts.targetGroups.indirect', 'Indirect Beneficiaries'),
      groups: [
        t('programs.sportCultureArts.targetGroups.indirect1', 'Local communities'),
        t('programs.sportCultureArts.targetGroups.indirect2', 'Sports federations'),
        t('programs.sportCultureArts.targetGroups.indirect3', 'Cultural institutions')
      ],
      icon: <Globe className="h-6 w-6" />
    }
  ];

  const activities = [
    {
      title: t('programs.sportCultureArts.activities.sports', 'Sports Activities'),
      items: [
        t('programs.sportCultureArts.activities.sports1', 'Football training and competitions'),
        t('programs.sportCultureArts.activities.sports2', 'Basketball development programs'),
        t('programs.sportCultureArts.activities.sports3', 'Athletics training'),
        t('programs.sportCultureArts.activities.sports4', 'Sports tournaments and leagues')
      ],
      icon: <Trophy className="h-6 w-6" />
    },
    {
      title: t('programs.sportCultureArts.activities.cultural', 'Cultural Activities'),
      items: [
        t('programs.sportCultureArts.activities.cultural1', 'Traditional dance performances'),
        t('programs.sportCultureArts.activities.cultural2', 'Cultural festivals and events'),
        t('programs.sportCultureArts.activities.cultural3', 'Heritage preservation workshops'),
        t('programs.sportCultureArts.activities.cultural4', 'Cultural exchange programs')
      ],
      icon: <Heart className="h-6 w-6" />
    },
    {
      title: t('programs.sportCultureArts.activities.arts', 'Arts Activities'),
      items: [
        t('programs.sportCultureArts.activities.arts1', 'Visual arts workshops'),
        t('programs.sportCultureArts.activities.arts2', 'Music and dance classes'),
        t('programs.sportCultureArts.activities.arts3', 'Theater performances'),
        t('programs.sportCultureArts.activities.arts4', 'Art exhibitions and showcases')
      ],
      icon: <BookOpen className="h-6 w-6" />
    }
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 py-12">
      {/* Hero Section */}
      <section className="relative mb-16 rounded-2xl overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-900 to-blue-800 z-10" />
        <div className="relative z-20 p-12 text-white">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-block p-3 bg-white/20 backdrop-blur-sm rounded-full mb-6">
              <Trophy className="h-8 w-8 text-white" />
            </div>
            <h1 className="text-5xl font-bold mb-6 text-white">
              {t('programs.sportCultureArts.title', 'Sports, Culture & Arts Program')}
            </h1>
            <p className="text-xl mb-8 text-blue-50 leading-relaxed">
              {t('programs.sportCultureArts.description', 'Our program nurtures youth talents in sports, preserves cultural heritage, and promotes artistic expression. Through various activities, we provide platforms for young people to develop their skills and showcase their abilities.')}
            </p>
            <div className="flex flex-wrap justify-center gap-6">
              <div className="flex items-center space-x-3 bg-white/10 backdrop-blur-sm px-6 py-3 rounded-full">
                <Trophy className="h-6 w-6" />
                <div>
                  <div className="text-2xl font-bold">3+</div>
                  <div className="text-sm text-gray-200">{t('programs.sportCultureArts.stats.activities', 'Activities')}</div>
                </div>
              </div>
              <div className="flex items-center space-x-3 bg-white/10 backdrop-blur-sm px-6 py-3 rounded-full">
                <Users className="h-6 w-6" />
                <div>
                  <div className="text-2xl font-bold">500+</div>
                  <div className="text-sm text-gray-200">{t('programs.sportCultureArts.stats.participants', 'Participants')}</div>
                </div>
              </div>
              <div className="flex items-center space-x-3 bg-white/10 backdrop-blur-sm px-6 py-3 rounded-full">
                <Award className="h-6 w-6" />
                <div>
                  <div className="text-2xl font-bold">20+</div>
                  <div className="text-sm text-gray-200">{t('programs.sportCultureArts.stats.events', 'Events')}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What We Offer Section */}
      <section className="bg-gradient-to-br from-blue-50 to-white rounded-2xl shadow-xl p-8 mb-16">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-blue-900 mb-6">
            {t('programs.sportCultureArts.whatWeOffer', 'What We Offer')}
          </h2>
          <div className="space-y-6">
            {programDetails.map((detail, index) => (
              <p key={index} className="text-lg text-gray-700 leading-relaxed">
                {detail}
              </p>
            ))}
          </div>
        </div>
      </section>

      {/* Objectives Section */}
      <section className="mb-16">
        <h2 className="text-3xl font-bold text-blue-900 mb-8 text-center">
          {t('programs.sportCultureArts.objectives.title', 'Program Objectives')}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {objectives.map((objective, index) => (
            <div key={index} className="bg-white rounded-2xl shadow-xl p-8">
              <div className="flex items-center mb-4">
                <div className="p-3 bg-blue-100 rounded-xl mr-4">
                  {objective.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900">
                  {objective.title}
                </h3>
              </div>
              <p className="text-gray-700 leading-relaxed">
                {objective.description}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Target Groups Section */}
      <section className="mb-16">
        <h2 className="text-3xl font-bold text-blue-900 mb-8 text-center">
          {t('programs.sportCultureArts.targetGroups.title', 'Target Groups')}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {targetGroups.map((group, index) => (
            <div key={index} className="bg-white rounded-2xl shadow-xl p-8">
              <div className="flex items-center mb-4">
                <div className="p-3 bg-blue-100 rounded-xl mr-4">
                  {group.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900">
                  {group.title}
                </h3>
              </div>
              <ul className="space-y-3">
                {group.groups.map((item, itemIndex) => (
                  <li key={itemIndex} className="flex items-start">
                    <div className="h-2 w-2 bg-blue-600 rounded-full mt-2 mr-3" />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* Activities Section */}
      <section className="mb-16">
        <h2 className="text-3xl font-bold text-blue-900 mb-8 text-center">
          {t('programs.sportCultureArts.activities.title', 'Our Activities')}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {activities.map((activity, index) => (
            <div key={index} className="bg-white rounded-2xl shadow-xl p-8">
              <div className="flex items-center mb-4">
                <div className="p-3 bg-blue-100 rounded-xl mr-4">
                  {activity.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900">
                  {activity.title}
                </h3>
              </div>
              <ul className="space-y-3">
                {activity.items.map((item, itemIndex) => (
                  <li key={itemIndex} className="flex items-start">
                    <div className="h-2 w-2 bg-blue-600 rounded-full mt-2 mr-3" />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="mt-16 bg-gradient-to-r from-blue-700 to-blue-900 rounded-2xl shadow-xl p-8">
        <div className="text-center">
          <h2 className="text-3xl font-bold mb-4 text-white">
            {t('programs.sportCultureArts.cta.title', 'Get Involved')}
          </h2>
          <p className="text-blue-50 mb-8 max-w-2xl mx-auto">
            {t('programs.sportCultureArts.cta.description', 'Join us in our mission to nurture youth talents and preserve cultural heritage. Your support can make a difference.')}
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/volunteer">
              <Button className="bg-white text-blue-600 hover:bg-blue-50">
                {t('programs.sportCultureArts.cta.volunteer', 'Volunteer With Us')}
              </Button>
            </Link>
            <Link to="/donate">
              <Button variant="outline" className="border-white text-white hover:bg-blue-700">
                {t('programs.sportCultureArts.cta.donate', 'Support Our Programs')}
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SportCultureArts; 